
import type { ModelOption } from "../shared/model-option.js";
import { IconCheck } from "./icons/check.js";
import { IconChevronDown } from "./icons/chevron-down.js";
import { IconEye } from "./icons/eye.js";

const styles = css`
  :host { display: block; min-width: 0; position: relative; }
  .trigger { display: block; min-width: 0; }
  .trigger-button { align-items: center; background: transparent; border: 0; border-radius: 8px; color: var(--prompt-control-fg, #efefef); cursor: pointer; display: flex; font: inherit; font-size: 1rem; font-weight: 500; height: 40px; padding: 0 4px; }
  .trigger-button:hover { background: var(--prompt-control-hover-bg, #303030); }
  .trigger-button:focus-visible { box-shadow: 0 0 0 3px var(--prompt-ring, rgb(255 255 255 / 7%)); outline: none; }
  .trigger-button:disabled { cursor: not-allowed; opacity: 0.5; }
  .trigger-content { align-items: center; display: flex; gap: 10px; min-width: 0; width: 100%; }
  .trigger-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .effort { color: var(--prompt-muted, #a0a0a0); font-weight: 400; }
  .trigger app-button icon-chevron-down { color: var(--prompt-muted, #a0a0a0); height: 18px; margin-left: 2px; width: 18px; }
  .popup { background: var(--popover, #292929); border: 1px solid var(--prompt-control-border, #3a3a3a); border-radius: 12px; bottom: calc(100% + 8px); box-shadow: 0 16px 36px rgb(0 0 0 / 30%); box-sizing: border-box; display: none; max-height: min(360px, 50vh); min-width: min(340px, calc(100vw - 32px)); overflow-y: auto; padding: 4px; position: absolute; right: 0; width: min(420px, calc(100vw - 32px)); z-index: 30; }
  :host([open]) .popup { display: block; }
  .empty { color: var(--muted-foreground); font-size: 0.8125rem; padding: 10px; }
  :host([disabled]) { opacity: 0.5; pointer-events: none; }
  @media (max-width: 640px) { .trigger { max-width: 180px; } .popup { min-width: min(300px, calc(100vw - 32px)); } }
`;

const optionStyles = css`
  :host { display: block; }
  button { align-items: flex-start; background: transparent; border: 0; border-radius: 9px; color: var(--popover-foreground, var(--foreground)); cursor: pointer; display: grid; font: inherit; gap: 3px; grid-template-columns: minmax(0, 1fr) 18px; padding: 10px 11px; text-align: left; width: 100%; }
  button:hover, button:focus-visible { background: var(--accent, #373737); outline: none; }
  .name-line { align-items: center; display: flex; font-size: 0.9375rem; font-weight: 600; gap: 6px; line-height: 1.25; min-width: 0; }
  .name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .vision { color: var(--muted-foreground, #a3a3a3); display: inline-flex; flex: 0 0 auto; }
  .description { color: var(--muted-foreground, #a3a3a3); font-size: 0.75rem; grid-column: 1 / 2; line-height: 1.35; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .check { align-items: center; align-self: center; color: var(--primary, #fafafa); display: none; grid-column: 2; grid-row: 1 / 3; height: 18px; justify-content: center; width: 18px; }
  button[aria-selected="true"] .check { display: inline-flex; }
  .sr-only { border: 0; clip: rect(0, 0, 0, 0); height: 1px; margin: -1px; overflow: hidden; padding: 0; position: absolute; white-space: nowrap; width: 1px; }
`;

function chooseModel(host: HTMLElement, event: Event): void {
  host.removeAttribute("open");
  host.dispatchEvent(new CustomEvent("value-change", {
    bubbles: true,
    composed: true,
    detail: (event as CustomEvent<{ value: string }>).detail,
  }));
}

export class ModelPickerOption extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof ModelPickerOption)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["model","data-model","selected","data-selected"];

  #model;
  #selected;
  #modelSet = false;
  #selectedSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #textStart2;
  #textEnd2;
  #textLast2;
  #textStart3;
  #textEnd3;
  #textLast3;
  #attr0;
  #attrLast0;
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get model() {
    return this.#model;
  }

  set model(value) {
    const raw = value;
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#model, next)) return;
    this.#model = next;
    this.#modelSet = true;
    this.#updateBindings('model');
  }

  get selected() {
    return this.#selected;
  }

  set selected(value) {
    const raw = value;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#selected, next)) return;
    this.#selected = next;
    this.#selectedSet = true;
    this.#updateBindings('selected');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-model')) this.model = this.dataset.model;
    if (this.hasAttribute('data-selected')) this.selected = this.dataset.selected;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.model = props.model;
      this.selected = props.selected;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (this.#isCSR || this.#bindingsMounted) return;
    this.#syncHydratedProps();
    if (!this.#modelSet) this.model = this.dataset.model;
    if (!this.#selectedSet) this.selected = this.dataset.selected;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'model':
      case 'data-model':
        this.model = newValue;
        break;
      case 'selected':
      case 'data-selected':
        this.selected = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    
    return `<style>${this.#escText(optionStyles)}</style><button type="button" role="option" aria-selected="${this.#escAttr(this.#selected ? "true" : "false")}" data-argon="3896" data-argon-bind="0"><span class="name-line"><span class="name"><!--argon-text:0-->${this.#escText(this.#model.label)}<!--/argon-text:0--></span><!--argon-text:1-->${this.#model.vision ? `<span class="vision" title="Supports vision"><icon-eye>${""}</icon-eye><span class="sr-only">Supports vision</span></span>` : ""}<!--/argon-text:1--></span><span class="description"><!--argon-text:2-->${this.#escText(this.#model.description || "No description available.")}<!--/argon-text:2--></span><span class="check"><icon-check><!--argon-text:3-->${""}<!--/argon-text:3--></icon-check></span></button>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
        case 'argon-text:2':
          this.#textStart2 = marker;
          break;
        case '/argon-text:2':
          this.#textEnd2 = marker;
          break;
        case 'argon-text:3':
          this.#textStart3 = marker;
          break;
        case '/argon-text:3':
          this.#textEnd3 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    
    if (prop === undefined || prop === 'model') {
      const __argonNext = `${this.#escText(this.#model.label)}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'model') {
      const __argonNext = `${this.#model.vision ? `<span class="vision" title="Supports vision"><icon-eye>${""}</icon-eye><span class="sr-only">Supports vision</span></span>` : ""}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if (prop === undefined || prop === 'model') {
      const __argonNext = `${this.#escText(this.#model.description || "No description available.")}`;
      if (__argonNext !== this.#textLast2) {
        this.#textLast2 = __argonNext;
        this.#replaceRange(this.#textStart2, this.#textEnd2, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast3) {
        this.#textLast3 = __argonNext;
        this.#replaceRange(this.#textStart3, this.#textEnd3, __argonNext);
      }
    }
    if ((prop === undefined || prop === 'selected') && this.#attr0) {
      const __argonNext = `${this.#selected ? "true" : "false"}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('aria-selected', __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '3896').forEach(el => this.#listen(el, '3896', 'click', () => this.dispatchEvent(new CustomEvent("model-select", { bubbles: true, composed: true, detail: { value: this.#model.value } }))));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}

export class AppModelPicker extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppModelPicker)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["models","data-models","value","data-value","label","data-label","reasoningeffort","data-reasoning-effort","disabled","data-disabled"];

  #models = [];
  #value = "";
  #label = "Choose model";
  #reasoningEffort = "";
  #disabled = false;
  #open;
  #modelsSet = false;
  #valueSet = false;
  #labelSet = false;
  #reasoningEffortSet = false;
  #disabledSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #textStart2;
  #textEnd2;
  #textLast2;
  #textStart3;
  #textEnd3;
  #textLast3;
  #attr0;
  #attrLast0;
  #eventContexts = {};
  #hooksLive = false;
  #cleanups = [];
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get models() {
    return this.#models;
  }

  set models(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#models, next)) return;
    this.#models = next;
    this.#modelsSet = true;
    this.#updateBindings('models');
  }

  get value() {
    return this.#value;
  }

  set value(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#value, next)) return;
    this.#value = next;
    this.#valueSet = true;
    this.#updateBindings('value');
  }

  get label() {
    return this.#label;
  }

  set label(value) {
    const raw = value ?? "Choose model";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#label, next)) return;
    this.#label = next;
    this.#labelSet = true;
    this.#updateBindings('label');
  }

  get reasoningEffort() {
    return this.#reasoningEffort;
  }

  set reasoningEffort(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#reasoningEffort, next)) return;
    this.#reasoningEffort = next;
    this.#reasoningEffortSet = true;
    this.#updateBindings('reasoningEffort');
  }

  get disabled() {
    return this.#disabled;
  }

  set disabled(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#disabled, next)) return;
    this.#disabled = next;
    this.#disabledSet = true;
    this.#updateBindings('disabled');
  }

  #setOpen(value) {
    if (Object.is(this.#open, value)) return;
    this.#open = value;
    this.#updateBindings('open');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-models')) this.models = this.dataset.models;
    if (this.hasAttribute('data-value')) this.value = this.dataset.value;
    if (this.hasAttribute('data-label')) this.label = this.dataset.label;
    if (this.hasAttribute('data-reasoning-effort')) this.reasoningEffort = this.dataset.reasoningEffort;
    if (this.hasAttribute('data-disabled')) this.disabled = this.dataset.disabled;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.models = props.models;
      this.value = props.value;
      this.label = props.label;
      this.reasoningEffort = props.reasoningEffort;
      this.disabled = props.disabled;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    if (!this.#modelsSet) this.models = this.dataset.models;
    if (!this.#valueSet) this.value = this.dataset.value;
    if (!this.#labelSet) this.label = this.dataset.label;
    if (!this.#reasoningEffortSet) this.reasoningEffort = this.dataset.reasoningEffort;
    if (!this.#disabledSet) this.disabled = this.dataset.disabled;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    {
      const cleanup = (() => {
    const close = (event: Event) => {
        if (this.#open && !event.composedPath().includes(this)) {
            this.#setOpen(false);
            this.removeAttribute("open");
        }
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
})();
      if (typeof cleanup === 'function') this.#cleanups.push(cleanup);
    }
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    for (const cleanup of this.#cleanups.splice(0)) cleanup();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'models':
      case 'data-models':
        this.models = newValue;
        break;
      case 'value':
      case 'data-value':
        this.value = newValue;
        break;
      case 'label':
      case 'data-label':
        this.label = newValue;
        break;
      case 'reasoningeffort':
      case 'data-reasoning-effort':
        this.reasoningEffort = newValue;
        break;
      case 'disabled':
      case 'data-disabled':
        this.disabled = newValue;
        break;
    }
  }

  #initState() {
    this.#open = false;
  }

  #render() {
    const hasReasoningEffort = this.#reasoningEffort !== "";
    return `<style>${this.#escText(styles)}</style><div class="trigger"><button class="trigger-button" type="button"${this.#disabled ? ' disabled' : ''} aria-label="Choose model" data-argon="5145" data-argon-bind="0"><span class="trigger-content"><span class="trigger-label"><!--argon-text:0-->${this.#escText(this.#label)}<!--/argon-text:0--></span><!--argon-text:1-->${hasReasoningEffort ? `<span class="effort">${this.#escText(this.#reasoningEffort)}</span>` : ""}<!--/argon-text:1--><icon-chevron-down><!--argon-text:2-->${""}<!--/argon-text:2--></icon-chevron-down></span></button></div><div class="popup" role="listbox" aria-label="Models"><!--argon-text:3-->${(this.#models.length === 0) ? "<div class=\"empty\">No models available</div>" : this.#models.map((model, __argonIndex) => this.#mapEventItem(`<model-picker-option data-model="${this.#attrEsc(model)}" data-selected="${this.#attrEsc(model.value === this.#value)}" data-argon="5712">${""}</model-picker-option>`, "initial:5:5712:" + __argonIndex, { 5712: (event: Event) => { this.#setOpen(false); chooseModel(this, event); } }, ["5712"])).join("")}<!--/argon-text:3--></div>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
        case 'argon-text:2':
          this.#textStart2 = marker;
          break;
        case '/argon-text:2':
          this.#textEnd2 = marker;
          break;
        case 'argon-text:3':
          this.#textStart3 = marker;
          break;
        case '/argon-text:3':
          this.#textEnd3 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #attrRaw(v) {
    return typeof v === 'object' ? JSON.stringify(v) : String(v);
  }

  #attrEsc(v) {
    return this.#attrRaw(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
  }

  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      this.#eventHandler(target, id, handler).call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #mapEventItem(html, ctx, handlers, ids) {
    const key = String(ctx);
    this.#eventContexts[key] = handlers;
    const attr = key.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
    let out = String(html);
    for (const id of ids) out = out.replaceAll(`data-argon="${id}"`, `data-argon="${id}" data-argon-c="${attr}"`);
    return out;
  }

  #eventHandler(el, id, fallback) {
    const ctx = el.getAttribute('data-argon-c');
    return ctx && this.#eventContexts[ctx]?.[id] ? this.#eventContexts[ctx][id] : fallback;
  }


  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const hasReasoningEffort = (prop === undefined || prop === 'reasoningEffort') ? (this.#reasoningEffort !== "") : undefined;
    if (prop === undefined || prop === 'label') {
      const __argonNext = `${this.#escText(this.#label)}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'reasoningEffort') {
      const __argonNext = `${hasReasoningEffort ? `<span class="effort">${this.#escText(this.#reasoningEffort)}</span>` : ""}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast2) {
        this.#textLast2 = __argonNext;
        this.#replaceRange(this.#textStart2, this.#textEnd2, __argonNext);
      }
    }
    if (prop === undefined || prop === 'models' || prop === 'value') {
      const __argonNext = `${(this.#models.length === 0) ? "<div class=\"empty\">No models available</div>" : this.#models.map((model, __argonIndex) => this.#mapEventItem(`<model-picker-option data-model="${this.#attrEsc(model)}" data-selected="${this.#attrEsc(model.value === this.#value)}" data-argon="5712">${""}</model-picker-option>`, "3:5:5712:" + __argonIndex, { 5712: (event: Event) => { this.#setOpen(false); chooseModel(this, event); } }, ["5712"])).join("")}`;
      if (__argonNext !== this.#textLast3) {
        this.#textLast3 = __argonNext;
        this.#replaceRange(this.#textStart3, this.#textEnd3, __argonNext);
      }
    }
    if ((prop === undefined || prop === 'disabled') && this.#attr0) {
      this.#attr0.toggleAttribute('disabled', !!(this.#disabled));
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '5145').forEach(el => this.#listen(el, '5145', 'click', () => { this.#setOpen(!this.#open); this.toggleAttribute("open", this.#open); }));
    this.#eventTargets(scope, '5712').forEach(el => this.#listen(el, '5712', 'model-select', (event: Event) => { this.#setOpen(false); chooseModel(this, event); }));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
